import * as React from 'react';
import {SyntheticEvent, useState} from "react";
import {withNamespaces, WithNamespaces} from "react-i18next";
import {useDispatch} from "react-redux";
import {Button, Confirm, Dropdown, Label} from "semantic-ui-react";
import {useOnBodyClick} from "../../core/hooks/useOnBodyClick";
import * as UserModule from "../../User";
import {IioUserInfo} from "../../User/model";
import {Action} from "../index";
import {OrganizationId} from "../model/Organization";

interface OwnProps {
    isOwner: boolean;
    isAdmin: boolean;
    organizationId: OrganizationId;
    currentUserIsOwner: boolean;
    member: IioUserInfo.IioUserInfo;
    readonly?: boolean;
    isGuest?: boolean;
}

type OrganizationMemberEntryProps = OwnProps & WithNamespaces;

const OrganizationMemberEntry = (props: OrganizationMemberEntryProps) => {
    const [showDropdown, setShowDropdown] = useState(false);
    const [avatarLoaded, setAvatarLoaded] = useState(false);
    const [showRemoveMemberConfirm, setShowRemoveMemberConfirm] = useState(false);
    const dispatch = useDispatch();

    useOnBodyClick(() => {
        setShowDropdown(false);
    });

    const canBeRemoved = (userId: UserModule.UserModel.UserId): boolean => {
        return !props.isOwner && !props.readonly;
    }

    const handleMemberClick = (e: SyntheticEvent) => {
        if(canBeRemoved(props.member.userId)) {
            e.stopPropagation();
            setShowDropdown(!showDropdown);
        }
    };

    const promoteMemberToAdmin = () => {
        if(!props.readonly) {
            dispatch(Action.Command.promoteUserToOrganizationAdmin(props.organizationId, props.member.userId));
        }
    }

    const demoteUserToMember = () => {
        if(!props.readonly) {
            dispatch(Action.Command.demoteUserToOrganizationMember(props.organizationId, props.member.userId));
        }
    }

    const turnGuestIntoMember = () => {
        if(!props.readonly) {
            dispatch(Action.Command.turnGuestIntoMember(props.member.userId, props.organizationId))
        }
    }

    const removeMemberFromOrganization = () => {
        if(!props.readonly) {
            if(props.isGuest) {
                dispatch(Action.Command.removeGuestFromOrganization(props.organizationId, props.member.userId));
            } else {
                dispatch(Action.Command.removeUserFromOrganization(props.organizationId, props.member.userId));
            }

        }
        setShowRemoveMemberConfirm(false);
    };

    const adminClass = props.isOwner || props.isAdmin? 'admin-member' : '';

    return <div className={"item " + adminClass} key={props.member.userId} style={canBeRemoved(props.member.userId) ? {cursor: "pointer"} : {}}>
        <Dropdown open={showDropdown} icon={false} pointing="top" disabled={props.readonly} className="organization">
            <Dropdown.Menu>
                {props.currentUserIsOwner && props.isAdmin && <Dropdown.Item
                    text={props.t('insp.organisation.demote_to_member')}
                    onClick={() => demoteUserToMember()}
                    />}
                {props.currentUserIsOwner && !props.isAdmin && !props.isGuest && <Dropdown.Item
                    text={props.t('insp.organisation.promote_to_admin')}
                    onClick={() => promoteMemberToAdmin()}
                />}
                {props.isGuest && <Dropdown.Item
                    text={props.t('insp.organisation.promote_to_member')}
                    onClick={() => turnGuestIntoMember()}
                />}
                <Dropdown.Item
                    text={props.t('app.form.remove')}
                    onClick={() => setShowRemoveMemberConfirm(true)}
                />
            </Dropdown.Menu>
        </Dropdown>
        {!avatarLoaded && <span className="ui avatar image placeholder" onClick={handleMemberClick} />}
        <img className="ui avatar image" src={props.member.avatar()} onClick={handleMemberClick}
                              onLoad={() => setAvatarLoaded(true)}
                              alt={props.member.username + " Avatar"} style={{display: avatarLoaded ? 'inline-block' : 'none'}}/>
        <div className="content" onClick={handleMemberClick}>
            <div className="header">{props.member.username}</div>
        </div>
        <Confirm
            open={!props.readonly && showRemoveMemberConfirm}
            header={props.t('insp.organisation.remove_user_confirm_title')}
            content={props.t(
                'insp.organisation.remove_user_confirm_text',
                { user_name: props.member.username, email: props.member.email }
            )}
            confirmButton={props.t('insp.organisation.remove_user_confirm_button')}
            cancelButton={<Button content={props.t('app.form.cancel')} className="basic noborder" />}
            onCancel={() => setShowRemoveMemberConfirm(false)}
            onConfirm={removeMemberFromOrganization}
        />
    </div>
};

export default withNamespaces()(OrganizationMemberEntry);
